import { formatDateRange } from '@spotify/confidence-core-react';
import {
  ActivityChangeOptions,
  ActivityItem,
  ActivityTagItem,
} from '@spotify/plugin-confidence-activity-react';
import { createWorkflowUpdatesChangeExtension } from '@spotify/plugin-confidence-workflows-react';

import { ReAnalysisData } from '../ReanalysisWorkflowInstancePage/ReanalysisModule/types';

const formatTimeRange = (start?: string, end?: string): string => {
  const rangeStart = start ?? end;
  const rangeEnd = end ?? start;
  if (rangeStart === undefined || rangeEnd === undefined) {
    return '';
  }
  return formatDateRange(new Date(rangeStart), new Date(rangeEnd));
};

const hasDatesChanged = (
  oldValue: ReAnalysisData | null,
  newValue: ReAnalysisData | null,
): boolean =>
  oldValue?.startTime !== newValue?.startTime ||
  oldValue?.endTime !== newValue?.endTime;

const ReanalysisConfigChangeRender = ({ change }: ActivityChangeOptions) => {
  const oldValue = change.oldValue as ReAnalysisData | null;
  const newValue = change.newValue as ReAnalysisData | null;

  return (
    <ActivityItem.Section label="Updated analysis period">
      <ActivityTagItem
        oldValue={
          formatTimeRange(oldValue?.startTime, oldValue?.endTime) || undefined
        }
        newValue={
          formatTimeRange(newValue?.startTime, newValue?.endTime) || undefined
        }
      />
    </ActivityItem.Section>
  );
};

// Suppress the /module_data/reanalysis date change when /module_data/planning
// also changed — the planning extension already shows the same dates.
export const REANALYSIS_CONFIG_SUPPRESS_EXTENSION =
  createWorkflowUpdatesChangeExtension({
    id: 'reanalysis-config-suppress',
    suppress: true,
    priority: 10,
    predicate: ({ change, activity }) => {
      if (change.fieldPath !== '/module_data/reanalysis') return false;
      const oldValue = change.oldValue as ReAnalysisData | null;
      const newValue = change.newValue as ReAnalysisData | null;
      return (
        hasDatesChanged(oldValue, newValue) &&
        (activity.changeSet?.changes.some(
          c => c.fieldPath === '/module_data/planning',
        ) ??
          false)
      );
    },
    render: () => null,
  });

// Render the date change when only /module_data/reanalysis changed.
export const REANALYSIS_CONFIG_CHANGE_EXTENSION =
  createWorkflowUpdatesChangeExtension({
    id: 'reanalysis-config-change',
    predicate: ({ change }) => {
      if (change.fieldPath !== '/module_data/reanalysis') return false;
      const oldValue = change.oldValue as ReAnalysisData | null;
      const newValue = change.newValue as ReAnalysisData | null;
      return hasDatesChanged(oldValue, newValue);
    },
    render: ReanalysisConfigChangeRender,
  });
